<template>

	<div>

		<!-- Loads List header -->
		<a-row type="flex" :gutter="24">
			<a-col :span="12" class="mb-24">
				<a-button v-if="isAdminUser" type="primary" @click="$router.push('/loads/create')">New Load</a-button>
			</a-col>
			<a-col :span="12" class="mb-24 text-right">
				<!-- WebSocket connection status indicator -->
				<ConnectionStatus :show-details="true" />
			</a-col>
		</a-row>
		<!-- / Loads List header -->

		<!-- Loads List card -->
		<a-card :bordered="false" class="header-solid mb-24" :bodyStyle="{ padding: 0, paddingTop: '16px' }">
			<!-- Tabs -->
			<div class="mx-25">
				<a-tabs v-model:activeKey="activeTab" type="card" @change="handleTabChange">
					<a-tab-pane key="active" tab="Active">
						<!-- Table search for Active tab -->
						<div class="mt-20">
							<a-row type="flex" :gutter="24">
								<a-col :span="12">
									<div class="table-controls">
										<span class="refresh-indicator">
											List last refreshed {{ lastRefreshText }}
										</span>
										<a-button 
											type="link" 
											@click="refreshTable" 
											:loading="isRefreshing"
											style="margin-left: 8px;"
										>
											<a-icon type="reload" />
											Refresh
										</a-button>
										<a-badge :count="newLoadsCount" :offset="[0, 0]" style="margin-left: 8px;">
											<a-button type="link" @click="showNewLoadsNotification">
												<a-icon type="notification" />
											</a-button>
										</a-badge>
									</div>
								</a-col>
								<a-col :span="12" class="text-right">
									<a-input-search placeholder="input search text" style="max-width: 200px;" v-model="query"
										@input="debouncedSearch" />
								</a-col>
							</a-row>
						</div>
					</a-tab-pane>
					<!-- Pending tab - only for admin users -->
					<a-tab-pane v-if="isAdminUser" key="pending" tab="Pending">
						<!-- Description for Pending tab -->
						<div class="mt-20 mb-20">
							<a-alert 
								message="Pending Loads" 
								description="This tab shows loads that are in pending status (bidding round ends in 5 minutes or less). These loads are not visible to users and will automatically move to completed status when bidding ends."
								type="warning" 
								show-icon
								:closable="false"
							/>
						</div>
						<!-- Table search for Pending tab -->
						<div class="mt-20">
							<a-row type="flex" :gutter="24">
								<a-col :span="12">
									<div class="table-controls">
										<span class="refresh-indicator">
											List last refreshed {{ lastRefreshText }}
										</span>
										<a-button 
											type="link" 
											@click="refreshTable" 
											:loading="isRefreshing"
											style="margin-left: 8px;"
										>
											<a-icon type="reload" />
											Refresh
										</a-button>
										<a-badge :count="newLoadsCount" :offset="[0, 0]" style="margin-left: 8px;">
											<a-button type="link" @click="showNewLoadsNotification">
												<a-icon type="notification" />
											</a-button>
										</a-badge>
									</div>
								</a-col>
								<a-col :span="12" class="text-right">
									<a-input-search placeholder="input search text" style="max-width: 200px;" v-model="query"
										@input="debouncedSearch" />
								</a-col>
							</a-row>
						</div>
					</a-tab-pane>
					<!-- My Active Bids tab - only for non-admin users -->
					<a-tab-pane v-if="!isAdminUser" key="my-bids" tab="My Active Bids">
						<!-- Description for My Active Bids tab -->
						<div class="mt-20 mb-20">
							<a-alert 
								message="My Active Bids" 
								description="This tab shows all active loads where you have placed bids. You can track your bidding activity and see the current status of your bids."
								type="info" 
								show-icon
								:closable="false"
							/>
						</div>
						<!-- Table search for My Active Bids tab -->
						<div class="mt-20">
							<a-row type="flex" :gutter="24">
								<a-col :span="12">
									<div class="table-controls">
										<span class="refresh-indicator">
											List last refreshed {{ lastRefreshText }}
										</span>
										<a-button 
											type="link" 
											@click="refreshTable" 
											:loading="isRefreshing"
											style="margin-left: 8px;"
										>
											<a-icon type="reload" />
											Refresh
										</a-button>
										<a-badge :count="newLoadsCount" :offset="[0, 0]" style="margin-left: 8px;">
											<a-button type="link" @click="showNewLoadsNotification">
												<a-icon type="notification" />
											</a-button>
										</a-badge>
									</div>
								</a-col>
								<a-col :span="12" class="text-right">
									<a-input-search placeholder="input search text" style="max-width: 200px;" v-model="query"
										@input="debouncedSearch" />
								</a-col>
							</a-row>
						</div>
					</a-tab-pane>
					<a-tab-pane key="other" tab="History">
						<!-- Table search for History tab -->
						<div class="mt-20">
							<a-row type="flex" :gutter="24">
								<a-col :span="12">
									<div class="table-controls">
										<span class="refresh-indicator">
											List last refreshed {{ lastRefreshText }}
										</span>
										<a-button 
											type="link" 
											@click="refreshTable" 
											:loading="isRefreshing"
											style="margin-left: 8px;"
										>
											<a-icon type="reload" />
											Refresh
										</a-button>
										<a-badge :count="newLoadsCount" :offset="[0, 0]" style="margin-left: 8px;">
											<a-button type="link" @click="showNewLoadsNotification">
												<a-icon type="notification" />
											</a-button>
										</a-badge>
									</div>
								</a-col>
								<a-col :span="12" class="text-right">
									<a-input-search placeholder="input search text" style="max-width: 200px;" v-model="query"
										@input="debouncedSearch" />
								</a-col>
							</a-row>
						</div>
					</a-tab-pane>
				</a-tabs>
			</div>
			<!-- / Tabs -->

			<!-- Loads table -->
			<div class="mx-25">
				<div class="table-container">
					<a-table class="mt-20" :columns="tableColumns" :data-source="loads" rowKey="id"
				:pagination="paginationConfig"
				:loading="isNotLoading"
				:scroll="{ x: 'max-content' }"
				@change="handleTableChange">
				<template slot="action" slot-scope="text, record">
					<a-space>
						<a-button v-if="isAdminUser" type="primary" size="small" @click="$router.push(`/loads/edit/${record.id}`)">
							<a-icon type="edit" />
						</a-button>
						<a-popconfirm
							v-if="isAdminUser"
							title="Are you sure you want to delete this load?"
							ok-text="Yes"
							cancel-text="No"
							@confirm="deleteLoad(record.id)"
						>
							<a-button type="danger" size="small" :loading="deletingLoadId === record.id">
								<a-icon type="delete" />
							</a-button>
						</a-popconfirm>
					</a-space>
				</template>
				<!-- Pickup Date Column -->
				<template slot="pickup_date" slot-scope="pickup_date">
					{{ formatDate(pickup_date) }}
				</template>
				<!-- Bidding Ends Column -->
				<template slot="bidding_ends" slot-scope="bidding_ends, record">	
					<div v-if="bidding_ends !== null && bidding_ends !== undefined">
						<div class="countdown-timer" :class="getCountdownClass(bidding_ends, record.status)">
							{{ getCountdownText(bidding_ends, record.status) }}
						</div>
					</div>
					<span v-else>N/A</span>
				</template>
				<!-- External Load ID Column -->
				<template slot="external_load_id" slot-scope="external_load_id, record">
					<div style="display: flex; align-items: center; gap: 8px;">
						<!-- For admin users: show bids popup -->
						<a 
							v-if="isAdminUser"
							@click="openBidsPopup(record)" 
							style="color: #1890ff; cursor: pointer; text-decoration: underline;"
							:title="`View bids for ${external_load_id}`"
						>
							{{ external_load_id }}
						</a>
						<!-- For regular users: just show the external load ID as text -->
						<span v-else class="external-load-id">
							{{ external_load_id }}
						</span>
					</div>
				</template>
				<!-- Itinerary Steps Column -->
				<template slot="itinerary_stops" slot-scope="itinerary_stops, record">
					<LoadItinerarySteps 
						:itinerary-stops="itinerary_stops || []" 
						:current-status="record.status"
					/>
				</template>
				<!-- Status Column / Place Bid Button -->
				<template slot="status" slot-scope="status, record">
					<!-- For non-admin users: Show Place Bid button -->
					<div v-if="!isAdminUser">
						<a-button 
							v-if="record.status === 'active'" 
							type="primary" 
							size="small"
							@click="openBidOfferPopup(record)"
							:disabled="false"
						>
							Place Bid
						</a-button>
						<a-tag 
							v-else
							:color="getStatusColor(getPersonalizedStatus(record))"
						>
							{{ getPersonalizedStatus(record).toUpperCase() }}
						</a-tag>
					</div>
					<!-- For admin users: Show status tag -->
					<a-tag v-else :color="getStatusColor(status)">
						{{ status.toUpperCase() }}
					</a-tag>
				</template>
				<!-- Chat Column -->
				<template slot="chat" slot-scope="text, record">
					<a-button
						type="link"
						size="small"
						@click="openChatForLoad(record)"
					>
						<a-icon type="message" />
						Chat
					</a-button>
				</template>
				<!-- My Current Bid Column -->
				<template slot="current_user_bid" slot-scope="text, record">
					<div v-if="record.current_user_bid && record.current_user_bid.offer_amount">
						<span 
							:class="[
								'current-bid-amount', 
								{ 'cancelled-bid': record.current_user_bid.cancelled }
							]"
							:style="record.current_user_bid.cancelled ? 'color: #ff4d4f; text-decoration: line-through;' : ''"
						>
							${{ record.current_user_bid.offer_amount }}
						</span>
					</div>
					<span v-else class="no-bid">No bid</span>
				</template>
				<!-- All Bidders Column (Admin Only) -->
				<template slot="current_leader" slot-scope="text, record">
					<div class="current-leader-cell" :class="{ 'bids-updated': record.bidsUpdated }">
						<div v-if="record.status === 'active'">
							<!-- Show all bidders for active loads -->
							<div v-if="record.bids && record.bids.length > 0" class="all-bidders">
								<div v-for="(bid, index) in record.bids" :key="bid.id" 
								     :class="['bidder-item', { 'winner-bidder': isWinningBid(bid, record) }]">
									<span :class="bid.cancelled ? 'status-indicator cancelled' : 'status-indicator active'"></span>
									<span class="bidder-name">{{ bid.user.full_name }}</span>
									<span :class="bid.cancelled ? 'bid-amount cancelled' : 'bid-amount'">${{ bid.offer_amount }}</span>
								</div>
							</div>
							<span v-else class="no-bids">No bids yet</span>
						</div>
						<div v-else>
							<!-- Show all bidders for completed loads -->
							<div v-if="record.bids && record.bids.length > 0" class="all-bidders">
								<div v-for="(bid, index) in record.bids" :key="bid.id" 
								     :class="['bidder-item', { 'winner-bidder': isWinningBid(bid, record) }]">
									<span :class="bid.cancelled ? 'status-indicator cancelled' : 'status-indicator completed'"></span>
									<span class="bidder-name">{{ bid.user.full_name }}</span>
									<span :class="bid.cancelled ? 'bid-amount cancelled' : 'bid-amount'">${{ bid.offer_amount }}</span>
									<!-- TEMPORARY DEBUG: Show cancelled status -->
									<span v-if="bid.cancelled" class="cancelled-debug">CANCELLED</span>
								</div>
							</div>
							<span v-else class="no-winner">No bids</span>
						</div>
					</div>
				</template>
			</a-table>
				</div>
			</div>
		<!-- / Loads table -->

		</a-card>
		<!-- / Loads List card -->

		<!-- Bids Popup Modal -->
		<BidsPopup
			:visible.sync="bidsPopupVisible"
			:loadId="selectedLoadForBids ? selectedLoadForBids.id : null"
			:loadInfo="selectedLoadForBids"
			@close="closeBidsPopup"
		/>

		<!-- Bid Offer Popup Modal (for users) -->
		<BidOfferPopup
			:visible.sync="bidOfferPopupVisible"
			:loadId="selectedLoadForBids ? selectedLoadForBids.id : null"
			:loadInfo="selectedLoadForBids"
			@close="closeBidOfferPopup"
			@bid-submitted="onBidSubmitted"
			@bid-cancelled="onBidCancelled"
		/>

		<!-- Chat Panel (fixed bottom dock, resizable like VS Code's terminal) -->
		<ChatPanel
			v-if="showChatPanel"
			ref="chatPanel"
			:load-id="chatFilteredLoadId"
			@filter-cleared="onChatFilterCleared"
			@chat-selected="onChatSelected"
			@close-panel="closeChatPanel"
		/>

	</div>

</template>

<script>
import debounce from 'lodash/debounce';
import loadAPI from '../../api/load';
import bidAPI from '../../api/bid';
import ConnectionStatus from '../../components/WebSocket/ConnectionStatus.vue';
import BidsPopup from '../../components/Bids/BidsPopup.vue';
import BidOfferPopup from '../../components/Bids/BidOfferPopup.vue';
import LoadItinerarySteps from '../../components/Loads/LoadItinerarySteps.vue';
import ChatPanel from '../../components/Chat/ChatPanel.vue';
// Table columns
const columns = [
	{
		title: 'EXTERNAL LOAD ID',
		dataIndex: 'external_load_id',
		width: 80,
		ellipsis: true,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		scopedSlots: { customRender: 'external_load_id' },
	},
	{
		title: 'PICKUP DATE',
		dataIndex: 'scheduled_pickup_date_time',
		width: 100,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		scopedSlots: { customRender: 'pickup_date' },
	},
	{
		title: 'ORIGIN',
		dataIndex: 'origin_city_name',
		width: 100,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		customRender: (text, record) => `${record.origin_city_name}, ${record.origin_state_code}`,
	},
	{
		title: 'TOTAL DISTANCE',
		dataIndex: 'total_distance',
		width: 90,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		customRender: (text) => text,
	},
	{
		title: 'DESTINATION',
		dataIndex: 'destination_city_name',
		width: 100,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		customRender: (text, record) => `${record.destination_city_name}, ${record.destination_state_code}`,
	},
	{
		title: 'BIDDING ENDS IN',
		dataIndex: 'bidding_round_ends',
		width: 80,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		scopedSlots: { customRender: 'bidding_ends' },
	},
	// {
	// 	title: 'RATE ADJUSTMENT',
	// 	dataIndex: 'rate_adjustment_amount',
	// 	width: 100,
	// 	sorter: true,
	// 	sortDirections: ['descend', 'ascend'],
	// 	customRender: (text) => text ? `$${text}` : 'N/A',
	// },
	{
		title: 'IN TRANSIT STOPS',
		dataIndex: 'in_transit_stops',
		width: 80,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		customRender: (text) => text || 'None',
	},
	{
		title: 'ITINERARY',
		dataIndex: 'itinerary_stops',
		width: 280,
		scopedSlots: { customRender: 'itinerary_stops' },
	},
	{
		title: 'MY CURRENT BID',
		dataIndex: 'current_user_bid',
		width: 120,
		scopedSlots: { customRender: 'current_user_bid' },
	},
	{
		title: 'PLACE BID', // Will be dynamically changed to 'STATUS' for admin users
		dataIndex: 'status',
		width: 100,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		scopedSlots: { customRender: 'status' },
	},
	{
		title: 'CHAT',
		dataIndex: 'chat',
		width: 80,
		scopedSlots: { customRender: 'chat' },
	},
];

const HISTORY_STATUS_FILTERS = ['completed', 'auction_won', 'auction_lost'];

// Admin-only columns
const adminColumns = [
	...columns,
	{
		title: 'ALL BIDDERS',
		dataIndex: 'current_leader',
		width: 180,
		fixed: 'right',
		scopedSlots: { customRender: 'current_leader' },
	},
	{
		title: 'ACTION',
		dataIndex: 'action',
		scopedSlots: { customRender: 'action' },
		width: 80,
		fixed: 'right',
	},
];

export default {
	components: {
		ConnectionStatus,
		BidsPopup,
		BidOfferPopup,
		LoadItinerarySteps,
		ChatPanel
	},
	data() {
		return {

			// Table Loads
			loads: [],

			// Table page size
			pageSize: 10,

			// Table page skip
			currentPage: 1,

			totalCount: 0,

			// Table search query
			query: '',



			isNotLoading: false,

			debouncedSearch: () => { },

			// Timer for updating current time (every minute)
			currentTimeTimer: null,

			// Countdown timer for bidding rounds (updates every minute)
			countdownTimer: null,

			// Sorting state
			sortBy: null,
			sortOrder: null,

			// Bids popup state
			bidsPopupVisible: false,
			selectedLoadForBids: null,
			
			// Bid offer popup state (for users)
			bidOfferPopupVisible: false,
			
			// Refresh and notification state
			lastRefreshTime: new Date(),
			isRefreshing: false,
			newLoadsCount: 0,
			
			
			// Current time for reactive updates
			currentTime: Date.now(),
			
			// Delete loading state
			deletingLoadId: null,

			// Active tab for loads filtering
			activeTab: 'active',

			// Status filters for history tab
			selectedHistoryStatuses: [...HISTORY_STATUS_FILTERS],

			// Chat state
			chatFilteredLoadId: null,
			showChatPanel: true,

	}
	},
	computed: {
		// Check if current user is admin
		isAdminUser() {
			return this.$store.getters['auth/isAdmin'];
		},


		// Dynamic columns based on user type and current tab
		tableColumns() {
			if (!this.isAdminUser) {
				// For non-admin users, conditionally exclude bidding ends column in history tab
				let nonAdminColumns = [...columns];
				if (this.activeTab === 'other') {
					nonAdminColumns = nonAdminColumns.filter(col => col.dataIndex !== 'bidding_round_ends');
				}
				
				// Update the status column title and properties for non-admin users
				const statusColumnIndex = nonAdminColumns.findIndex(col => col.dataIndex === 'status');
				if (statusColumnIndex !== -1) {
					nonAdminColumns[statusColumnIndex] = {
						...nonAdminColumns[statusColumnIndex],
						title: 'PLACE BID',
						width: 120,
						sorter: false, // Disable sorting for Place Bid button
						filters: [], // Remove filters for Place Bid button
						onFilter: null,
					};
				}
				
				return nonAdminColumns;
			}
			
			// Create dynamic admin columns based on active tab
			let dynamicAdminColumns = [...columns];
			
			// Remove the "MY CURRENT BID" column for admin users
			dynamicAdminColumns = dynamicAdminColumns.filter(col => col.dataIndex !== 'current_user_bid');
			
			// Update the status column title back to STATUS for admin users
			const statusColumnIndex = dynamicAdminColumns.findIndex(col => col.dataIndex === 'status');
			if (statusColumnIndex !== -1) {
				const isHistoryTab = this.activeTab === 'other';
				const historyFilters = [
					{ text: 'Completed', value: 'completed' },
					{ text: 'Auction Won', value: 'auction_won' },
					{ text: 'Auction Lost', value: 'auction_lost' },
				];
				dynamicAdminColumns[statusColumnIndex] = {
					...dynamicAdminColumns[statusColumnIndex],
					title: 'STATUS',
					width: 100,
					sorter: true,
					sortDirections: ['descend', 'ascend'],
					filters: isHistoryTab ? historyFilters : [],
					filteredValue: isHistoryTab ? this.selectedHistoryStatuses : null,
					onFilter: null,
				};
			}
			
			// Remove bidding ends column for history tab since bidding has already ended
			if (this.activeTab === 'other') {
				dynamicAdminColumns = dynamicAdminColumns.filter(col => col.dataIndex !== 'bidding_round_ends');
			}
			
			// Remove status column for active and pending tabs (admins don't need it since they know the tab context)
			if (this.activeTab === 'active' || this.activeTab === 'pending') {
				dynamicAdminColumns = dynamicAdminColumns.filter(col => col.dataIndex !== 'status');
			}
			
			// Add admin-specific columns
			dynamicAdminColumns.push({
				title: 'ALL BIDDERS',
				dataIndex: 'current_leader',
				width: 200,
				scopedSlots: { customRender: 'current_leader' },
			});
			
			// Only show ACTION column for active and pending loads, not for history
			if (this.activeTab === 'active' || this.activeTab === 'pending') {
				dynamicAdminColumns.push({
					title: 'ACTION',
					dataIndex: 'action',
					scopedSlots: { customRender: 'action' },
					width: 140,
					fixed: 'right',
				});
			}
			
			return dynamicAdminColumns;
		},
		
		// Pagination configuration with validation
		paginationConfig() {
			// Disable pagination for active, my-bids, and pending tabs
			if (this.activeTab === 'active' || this.activeTab === 'my-bids' || this.activeTab === 'pending') {
				return false;
			}
			
			// Keep pagination for history tab (other)
			return {
				pageSize: this.pageSize || 10,
				current: this.currentPage || 1,
				total: this.totalCount || 0,
				onChange: this.handlePageChange,
				showSizeChanger: true,
				showQuickJumper: true,
				showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} loads`,
			};
		},

		// Refresh indicator text
		lastRefreshText() {
			const now = this.currentTime;
			const diffMs = now - this.lastRefreshTime.getTime();
			const diffSeconds = Math.floor(diffMs / 1000);
			const diffMinutes = Math.floor(diffMs / 60000);
			
			if (diffSeconds < 60) {
				return 'a few seconds ago';
			} else if (diffMinutes < 60) {
				return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
			} else {
				const diffHours = Math.floor(diffMs / 3600000);
				return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
			}
		},

		// Check if current user is admin
		isAdminUser() {
			return localStorage.getItem('userType') === 'admin';
		},

		// Get current user ID from stored user data
		currentUserId() {
			try {
				// Fallback to localStorage
				const userFromStorage = localStorage.getItem('user');
				console.log(userFromStorage)
				if (userFromStorage) {
					const user = JSON.parse(userFromStorage);
					return user.id;
				}
			} catch (error) {
				console.error('Error getting user data:', error);
			}
			return null;
		},

	},
	methods: {
		resetHistoryStatusFilters() {
			this.selectedHistoryStatuses = [...HISTORY_STATUS_FILTERS];
		},

		getHistoryStatusesToRequest() {
			if (this.selectedHistoryStatuses && this.selectedHistoryStatuses.length > 0) {
				return this.selectedHistoryStatuses.map(status => (status || '').toLowerCase());
			}
			return HISTORY_STATUS_FILTERS;
		},

		loadMatchesHistoryStatuses(load) {
			if (this.activeTab !== 'other') {
				return true;
			}
			const allowedStatuses = new Set(this.getHistoryStatusesToRequest().map(status => status.toLowerCase()));
			const statusToCheck = (this.isAdminUser ? load.status : this.getPersonalizedStatus(load)) || '';
			return allowedStatuses.has(statusToCheck.toLowerCase());
		},

		// Handle table changes (pagination, filters, sorting)
		async handleTableChange(pagination, filters, sorter) {
			// Update history status filters when applicable
			if (this.activeTab === 'other' && filters && Object.prototype.hasOwnProperty.call(filters, 'status')) {
				const filterValues = filters.status;
				if (Array.isArray(filterValues) && filterValues.length > 0) {
					this.selectedHistoryStatuses = filterValues.map(value => (value || '').toLowerCase());
				} else {
					this.resetHistoryStatusFilters();
				}
				this.currentPage = 1;
			}

			// Update pagination
			if (pagination.current !== this.currentPage) {
				this.currentPage = pagination.current;
			}
			if (pagination.pageSize !== this.pageSize) {
				this.pageSize = pagination.pageSize;
				this.currentPage = 1; // Reset to first page when page size changes
			}

			// Update sorting
			if (sorter && sorter.columnKey) {
				this.sortBy = sorter.columnKey;
				this.sortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
			} else if (sorter && sorter.field) {
				this.sortBy = sorter.field;
				this.sortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
			} else {
				this.sortBy = null;
				this.sortOrder = null;
			}

			// Fetch data with new parameters
			await this.fetchLoads();
		},


		async handlePageChange(page) {
			this.currentPage = page;
			await this.fetchLoads();
		},

		// Centralized method to fetch loads with all parameters
		async fetchLoads() {
			this.isNotLoading = true;
			
			// For tabs without pagination, use a large page size to get all records
			const shouldUsePagination = this.activeTab === 'other';
			const params = {
				page_size: shouldUsePagination ? this.pageSize : 0, // Large number to get all records
				page: shouldUsePagination ? this.currentPage : 1,
			};

			// Add search query if exists
			if (this.query && this.query.length >= 3) {
				params.query = this.query;
			}

			// Add sorting parameters if exists
			if (this.sortBy && this.sortOrder) {
				params.sort_by = this.sortBy;
				params.sort_order = this.sortOrder;
			}

			// Add status filtering based on active tab
			if (this.activeTab === 'active') {
				params.statuses = 'active';
			} else if (this.activeTab === 'pending') {
				params.statuses = 'pending';
			} else if (this.activeTab === 'my-bids') {
				params.statuses = 'active'; // Only active loads for my bids
			} else if (this.activeTab === 'other') {
				const statuses = this.getHistoryStatusesToRequest();
				params.statuses = statuses.join(',');
			}

			try {
				let response;
				
				// For regular users on my-bids tab or history tab, get loads where they have bids
				if (!this.isAdminUser && (this.activeTab === 'my-bids' || this.activeTab === 'other')) {
					response = await loadAPI.getLoadsWithUserBids({ params });
				} else {
					// For all other cases, use the regular loads API which now includes bids data
					response = await loadAPI.getLoads({ params });
				}
				
				let newLoads = response.data || [];
				
				// Handle different data structures based on API endpoint
				if (!this.isAdminUser && (this.activeTab === 'my-bids' || this.activeTab === 'other')) {
					// For my-bids and history tabs, extract load data from nested structure
					newLoads = newLoads.map(item => {
						if (item.load) {
							// Extract the load object and preserve any additional data
							const load = { ...item.load };
							// Add user bid information if available
							if (item.user_bid) {
								load.current_user_bid = item.user_bid;
							}
							// Also check if the load already has current_user_bid from backend
							// (this maintains backward compatibility)
							return load;
						}
						return item; // Fallback if structure is different
					});
				}
				
				// Preserve existing bid_count values from WebSocket updates
				const existingBidCounts = {};
				this.loads.forEach(load => {
					if (load.bid_count !== undefined && load.bid_count > 0) {
						existingBidCounts[load.external_load_id] = load.bid_count;
					}
				});
				
				// Merge bid_count values into new loads data
				newLoads.forEach(load => {
					if (existingBidCounts[load.external_load_id] !== undefined) {
						load.bid_count = existingBidCounts[load.external_load_id];
					}
				});

				// Ensure history tab enforces selected status filters on the displayed data as well
				if (this.activeTab === 'other') {
					newLoads = newLoads.filter(load => this.loadMatchesHistoryStatuses(load));
				}
				
				this.loads = newLoads;
				this.totalCount = response.pagination ? response.pagination.total : 0;
				
				// Update refresh time when data is fetched
				this.lastRefreshTime = new Date();
				this.updateCurrentTime(); // Update current time for refresh text
			} catch (error) {
				console.error('Error fetching loads:', error);
				this.loads = [];
				this.totalCount = 0;
			} finally {
				this.isNotLoading = false;
			}
		},



		// get loads according to filters (keep for backward compatibility)
		async getLoads(filter) {
			this.isNotLoading = true;
			return loadAPI.getLoads(filter);
		},

		// Event listener for input change on table search field.
		async onSearchChange() {
			this.currentPage = 1; // Reset to first page when searching
			await this.fetchLoads();
		},



		// Handle tab change
		async handleTabChange(activeKey) {
			this.activeTab = activeKey;
			if (activeKey !== 'other') {
				this.resetHistoryStatusFilters();
			}
			this.currentPage = 1; // Reset to first page when changing tabs
			await this.fetchLoads();
		},

		// Format date for display
		formatDate(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
		},

		// Format currency for display

		// Check if a bid is the winning bid (for highlighting in blue)
		isWinningBid(bid, load) {
			// Only highlight blue if load has an official designated winner
			if (load.winner && bid.user && load.winner.id === bid.user.id) {
				return true;
			}
			return false;
		},

		// Get personalized status for a user based on whether they won the auction
		getPersonalizedStatus(load) {
			// For admin users, always show actual load status
			if (this.isAdminUser) {
			return load.status;
			}

			// For history tab, show personalized status based on auction results
			if (this.activeTab === 'other') {
				// Check if this user won the auction
				if (load.winner && this.currentUserId && load.winner.id === this.currentUserId) {
					// User won - show actual status
					return load.status;
				} else if (load.current_user_bid || (load.bids && load.bids.some(bid => bid.user && bid.user.id === this.currentUserId))) {
					// User had a bid but didn't win - show auction_lost
					return 'auction_lost';
				}
			}
			
			// For other cases, show actual status
			return load.status;
		},


		// Get status color for tags
		getStatusColor(status) {
			const statusColors = {
				'pending': 'orange',
				'active': 'blue',
				'completed': 'brown',
				'cancelled': 'red',
				'in_progress': 'purple',
				'auction_lost': 'red',
				'auction_won': 'green',
				'removed_from_auction': 'volcano',
			};
			return statusColors[status.toLowerCase()] || 'default';
		},

		// Initialize WebSocket connection for loads
		initWebSocketLoads() {
			console.log('Initializing WebSocket connection for loads...');
			
			// Set up event listeners first
			this.$wsOnChannel('loads', 'new_load', this.onNewLoad);
			this.$wsOnChannel('loads', 'load_updated', this.onLoadUpdated);
			this.$wsOnChannel('loads', 'load_deleted', this.onLoadDeleted);
			
		// Listen to bid notifications and updates
		this.$wsOn('bid_notification', this.onBidNotification);
		this.$wsOn('bid_status_changed', this.onBidStatusChanged);
		this.$wsOn('new_bid', this.onNewBid);
		this.$wsOn('new_notification', this.onNewNotification);
		this.$wsOn('winner_selected', this.onWinnerSelected);
			
			// Listen to connection events
			this.$wsOn('connected', this.onWebSocketConnected);
			this.$wsOn('disconnected', this.onWebSocketDisconnected);
			this.$wsOn('error', this.onWebSocketError);
			
			// Subscribe to loads channel after setting up listeners
			this.$wsSubscribe('loads');
			
			console.log('WebSocket event listeners set up successfully');
		},

		// WebSocket event handlers
		onWebSocketConnected(event) {
			console.log('WebSocket connected - loads subscribed');
			this.$message.success('Real-time updates connected');
		},

		onWebSocketDisconnected(event) {
			console.log('WebSocket disconnected');
			this.$message.warning('Real-time updates disconnected');
		},

		onWebSocketError(event) {
			console.error('WebSocket error:', event);
			this.$message.error('WebSocket connection error. Please check your network and try again.');
		},

		// Handle new load received via WebSocket
		onNewLoad(data) {
			console.log('New load received:', data);
			
			// Handle loads_added field from WebSocket data
			if (data.loads_added && typeof data.loads_added === 'number') {
				this.newLoadsCount += data.loads_added;
			}
			
			// Check for load data in payload (new format) or load field (old format)
			const loadData = data.payload || data.load;
			
			if (loadData) {
				// Increment new loads counter (if not already handled by loads_added)
				if (!data.loads_added) {
					this.newLoadsCount += 1;
				}
			}
		},

		// Handle load update received via WebSocket
		onLoadUpdated(data) {
			console.log('Load updated:', data);
			
			// Check for load data in payload (new format) or load field (old format)
			const loadData = data.payload || data.load;
			
			if (loadData) {
				const index = this.loads.findIndex(load => load.id === loadData.id);
				if (index !== -1) {
					// Update the load in place
					this.$set(this.loads, index, loadData);
					this.$message.info(`Load updated: ${loadData.bid_id}`);
				}
			}
		},

		// Handle load deletion received via WebSocket
		onLoadDeleted(data) {
			// Check for load_id in payload (new format) or directly in data (old format)
			const loadId = data.payload?.id || data.load_id;
			const bidId = data.payload?.bid_id || data.bid_id;
			
			if (loadId) {
				const index = this.loads.findIndex(load => load.id === loadId);
				if (index !== -1) {
					this.loads.splice(index, 1);
					this.totalCount = Math.max(0, this.totalCount - 1);
					this.$message.info(`Load deleted: ${bidId || loadId}`);
				}
			}
		},

		cleanupWebSocketLoads() {
			this.$wsUnsubscribe('loads');
			// Clean up bid-related event listeners
			this.$wsOff('bid_notification', this.onBidNotification);
			this.$wsOff('bid_status_changed', this.onBidStatusChanged);
			this.$wsOff('new_bid', this.onNewBid);
			this.$wsOff('new_notification', this.onNewNotification);
			this.$wsOff('winner_selected', this.onWinnerSelected);
		},

		// Handle bid notifications received via WebSocket (new bids, updates, cancellations)
		onBidNotification(data) {
			const notification = data.payload || data;
			if (notification.external_load_id) {
				// Find the load in the current list
				const loadIndex = this.loads.findIndex(load => load.external_load_id === notification.external_load_id);
				if (loadIndex !== -1) {
					// Update bids data in real-time
					this.updateLoadBidsData(loadIndex, notification);
					
					// Show notification to user
					this.$message.info(`Bid update received for load ${notification.external_load_id}`);
				}
			}
		},

		// Handle bid status changes (updates, cancellations)
		onBidStatusChanged(data) {
			const bidData = data.payload || data;
			
			// Try to find the load by external_load_id first, then by load_id
			let loadIndex = -1;
			if (bidData.external_load_id) {
				loadIndex = this.loads.findIndex(load => load.external_load_id === bidData.external_load_id);
			} else if (bidData.load_id) {
				loadIndex = this.loads.findIndex(load => load.id === bidData.load_id);
			}
			
			if (loadIndex !== -1) {
				// Update bids data in real-time
				this.updateLoadBidsData(loadIndex, bidData);
				const loadExternalId = this.loads[loadIndex].external_load_id;
				this.$message.info(`Bid status updated for load ${loadExternalId}`);
			}
		},

		// Handle new bid events
		onNewBid(data) {
			const bidData = data.payload || data;
			
			// Try to find the load by external_load_id first, then by load_id
			let loadIndex = -1;
			if (bidData.external_load_id) {
				loadIndex = this.loads.findIndex(load => load.external_load_id === bidData.external_load_id);
			} else if (bidData.load_id) {
				loadIndex = this.loads.findIndex(load => load.id === bidData.load_id);
			} else if (bidData.load && bidData.load.external_load_id) {
				loadIndex = this.loads.findIndex(load => load.external_load_id === bidData.load.external_load_id);
			}
			
			if (loadIndex !== -1) {
				// Update bids data in real-time
				this.updateLoadBidsData(loadIndex, bidData);
				const loadExternalId = this.loads[loadIndex].external_load_id;
				this.$message.info(`New bid received for load ${loadExternalId}`);
			}
		},

		// Handle new notification events (bid updates, cancellations)
		onNewNotification(data) {
			const notification = data.payload || data;
			
			// Check if this is a bid-related notification
			if (notification.type === 'bid_updated' || notification.type === 'bid_cancelled') {
				// Find the load by load_id
				const loadIndex = this.loads.findIndex(load => load.id === notification.load_id);
				
				if (loadIndex !== -1) {
					// Create a notification data object for updateLoadBidsData
					const updateData = {
						bid_id: notification.bid_id,
						load_id: notification.load_id,
						action: notification.type === 'bid_cancelled' ? 'cancelled' : 'updated'
					};
					
					this.updateLoadBidsData(loadIndex, updateData);
					
					const loadExternalId = this.loads[loadIndex].external_load_id;
					this.$message.info(`Bid ${notification.type} for load ${loadExternalId}`);
				}
			}
		},

		// Handle winner selection events
		onWinnerSelected(data) {
			this.refreshAfterAction();		},

		// Update bids data for a specific load
		async updateLoadBidsData(loadIndex, notificationData) {
			try {
				// For immediate feedback, handle specific actions locally first
				if (notificationData.action === 'cancelled' && notificationData.bid_id && this.loads[loadIndex].bids) {
					// Update the specific bid locally for immediate visual feedback
					const bidIndex = this.loads[loadIndex].bids.findIndex(bid => bid.id === notificationData.bid_id);
					if (bidIndex !== -1) {
						this.$set(this.loads[loadIndex].bids[bidIndex], 'cancelled', true);
					}
				} else if (notificationData.deleted && notificationData.id && this.loads[loadIndex].bids) {
					// Handle bid deletion locally
					const bidIndex = this.loads[loadIndex].bids.findIndex(bid => bid.id === notificationData.id);
					if (bidIndex !== -1) {
						this.loads[loadIndex].bids.splice(bidIndex, 1);
					}
				} else if (notificationData.cancelled !== undefined && notificationData.id && this.loads[loadIndex].bids) {
					// Handle direct cancelled field update
					const bidIndex = this.loads[loadIndex].bids.findIndex(bid => bid.id === notificationData.id);
					if (bidIndex !== -1) {
						this.$set(this.loads[loadIndex].bids[bidIndex], 'cancelled', notificationData.cancelled);
					}
				}
				
				// Always fetch the latest bids data to ensure we have complete information
				// This is especially important for new bids that need user information
				const response = await bidAPI.getBidsByLoadId(this.loads[loadIndex].id);
				const bids = response.data || response || [];
				
				// Update the bids array in the load record
				this.$set(this.loads[loadIndex], 'bids', bids);
				
				// Update bid count
				this.$set(this.loads[loadIndex], 'bid_count', bids.length);
				
				// Add visual feedback for real-time update
				this.$set(this.loads[loadIndex], 'bidsUpdated', true);
				
				// Remove the visual feedback after a short delay
				setTimeout(() => {
					this.$set(this.loads[loadIndex], 'bidsUpdated', false);
				}, 2000);
				
			} catch (error) {
				console.error('Error updating bids data:', error);
				
				// For cancellation events, still try to update locally if we have the data
				if (notificationData.action === 'cancelled' && notificationData.bid_id && this.loads[loadIndex].bids) {
					const bidIndex = this.loads[loadIndex].bids.findIndex(bid => bid.id === notificationData.bid_id);
					if (bidIndex !== -1) {
						this.$set(this.loads[loadIndex].bids[bidIndex], 'cancelled', true);
					}
				} else if (notificationData.deleted && notificationData.id && this.loads[loadIndex].bids) {
					const bidIndex = this.loads[loadIndex].bids.findIndex(bid => bid.id === notificationData.id);
					if (bidIndex !== -1) {
						this.loads[loadIndex].bids.splice(bidIndex, 1);
					}
				}
				
				// Still add visual feedback even on error
				this.$set(this.loads[loadIndex], 'bidsUpdated', true);
				setTimeout(() => {
					this.$set(this.loads[loadIndex], 'bidsUpdated', false);
				}, 2000);
			}
		},

		openBidsPopup(load) {
			this.selectedLoadForBids = load;
			this.bidsPopupVisible = true;
		},

		closeBidsPopup() {
			this.bidsPopupVisible = false;
			this.selectedLoadForBids = null;
		},

		// Bid offer popup methods
		openBidOfferPopup(load) {
			this.selectedLoadForBids = load;
			this.bidOfferPopupVisible = true;
		},

		closeBidOfferPopup() {
			this.bidOfferPopupVisible = false;
			this.selectedLoadForBids = null;
		},

		async onBidSubmitted() {
			this.closeBidOfferPopup();
			await this.refreshAfterAction();
		},

		async onBidCancelled() {
			// Refresh the table to show updated bid status
			await this.refreshAfterAction();
		},

		async refreshAfterAction() {
			try {
				// Show a subtle loading state
				this.isNotLoading = true;
				
				// Add a small delay to ensure backend has processed the bid
				await new Promise(resolve => setTimeout(resolve, 500));
				
				// Silently refresh the table to show updated bid information
				await this.fetchLoads();
				this.lastRefreshTime = new Date();
				this.updateCurrentTime();
				
				// Show success message to confirm refresh
				this.$message.success('Bid submitted and table updated', 2);
			} catch (error) {
				console.error('Error refreshing loads after bid submission:', error);
				// Fallback to full refresh if silent refresh fails
				this.refreshTable();
			} finally {
				this.isNotLoading = false;
			}
		},

		async refreshTable() {
			this.isRefreshing = true;
			try {
				this.currentPage = 1; // Reset to first page when refreshing
				await this.fetchLoads();
				this.lastRefreshTime = new Date();
				this.updateCurrentTime();
				this.newLoadsCount = 0;
				this.$message.success('Table refreshed successfully');
			} catch (error) {
				console.error('Error refreshing table:', error);
				this.$message.error('Failed to refresh table');
			} finally {
				this.isRefreshing = false;
			}
		},

		showNewLoadsNotification() {
			if (this.newLoadsCount > 0) {
				this.$message.info(`${this.newLoadsCount} new load${this.newLoadsCount > 1 ? 's' : ''} added since last refresh`);
			}
		},

		updateCurrentTime() {
			this.currentTime = Date.now();
		},

		// Countdown timer methods
		startCountdownTimer() {
			this.countdownTimer = setInterval(() => {
				// Decrease all bidding_round_ends values by 1 minute
				this.loads.forEach(load => {
					if (load.bidding_round_ends !== null && load.bidding_round_ends !== undefined) {
						const currentValue = parseInt(load.bidding_round_ends);
						if (!isNaN(currentValue) && currentValue > 0) {
							// Use Vue.set to ensure reactivity
							this.$set(load, 'bidding_round_ends', currentValue - 1);
						}
					}
				});
			}, 60000); // Update every minute
		},

		stopCountdownTimer() {
			if (this.countdownTimer) {
				clearInterval(this.countdownTimer);
				this.countdownTimer = null;
			}
		},

		getCountdownText(biddingEndsMinutes, status) {
			if (biddingEndsMinutes === null || biddingEndsMinutes === undefined) return 'N/A';
			
			// Convert to number if it's a string
			let minutes = parseInt(biddingEndsMinutes);
			if (isNaN(minutes)) return 'N/A';
			
			// For regular users, show 5 minutes less than actual for active loads
			if (!this.isAdminUser && status === 'active') {
				minutes = Math.max(0, minutes - 5);
			}
			
			if (minutes <= 0) {
				return '00';
			}
			
			// Format as MM:SS or just MM if less than 60 minutes
			if (minutes >= 60) {
				const hours = Math.floor(minutes / 60);
				const remainingMinutes = minutes % 60;
				return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
			} else {
				return minutes.toString().padStart(2, '0');
			}
		},

		getCountdownClass(biddingEndsMinutes, status) {
			if (biddingEndsMinutes === null || biddingEndsMinutes === undefined) return '';
			
			// Convert to number if it's a string
			let minutes = parseInt(biddingEndsMinutes);
			if (isNaN(minutes)) return '';
			
			// For regular users, show 5 minutes less than actual for active loads
			if (!this.isAdminUser && status === 'active') {
				minutes = Math.max(0, minutes - 5);
			}
			
			if (minutes <= 0) {
				return 'countdown-expired';
			} else if (minutes > 20) { // More than 20 minutes
				return 'countdown-normal';
			} else if (minutes >= 10) { // Between 10-20 minutes
				return 'countdown-warning';
			} else { // Less than 10 minutes
				return 'countdown-urgent';
			}
		},

		startCurrentTimeTimer() {
			this.currentTimeTimer = setInterval(() => {
				this.updateCurrentTime();
			}, 60000);
		},

		stopCurrentTimeTimer() {
			if (this.currentTimeTimer) {
				clearInterval(this.currentTimeTimer);
				this.currentTimeTimer = null;
			}
		},
		
		// Delete load method
		async deleteLoad(loadId) {
			this.deletingLoadId = loadId;
			try {
				await loadAPI.deleteLoad(loadId);
				this.$message.success('Load deleted successfully');
				// Remove the load from the local array
				this.loads = this.loads.filter(load => load.id !== loadId);
				// Update total count
				this.totalCount = Math.max(0, this.totalCount - 1);
			} catch (error) {
				this.$message.error(error.response?.data?.error || 'Failed to delete load');
			} finally {
				this.deletingLoadId = null;
			}
		},

		// Chat methods
		filterChatsByLoad(loadId) {
			this.chatFilteredLoadId = loadId;
			// Scroll to chat panel
			this.$nextTick(() => {
				const chatCard = this.$el.querySelector('.chat-panel');
				if (chatCard) {
					chatCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			});
		},

		onChatFilterCleared() {
			this.chatFilteredLoadId = null;
		},

		onChatSelected(chat) {
			// Handle chat selection if needed
			console.log('Chat selected:', chat);
		},

		openChatForLoad(load) {
			// Re-open the dock if it was closed
			this.showChatPanel = true;
			this.chatFilteredLoadId = load.id;
			this.$nextTick(() => {
				// For users, open their own chat for this load
				if (!this.isAdminUser && this.$refs.chatPanel) {
					this.$refs.chatPanel.openChat(load.id, this.currentUserId);
				}
				// Scroll to chat panel
				const chatCard = this.$el.querySelector('.chat-panel');
				if (chatCard) {
					chatCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			});
		},

		closeChatPanel() {
			this.showChatPanel = false;
		},
	},
	async mounted() {
		this.debouncedSearch = debounce(this.onSearchChange, 300);
		
		await this.fetchLoads();

		this.initWebSocketLoads();
		
		this.startCurrentTimeTimer();
		this.startCountdownTimer();
	},

	beforeDestroy() {
		this.cleanupWebSocketLoads();
		
		this.stopCurrentTimeTimer();
		this.stopCountdownTimer();
	}
}

</script>

<style lang="scss" scoped>
.table-avatar-info {
	display: flex;
	align-items: center;
}

.table-avatar-info .ant-avatar {
	margin-right: 8px;
}

// Using vuejs "Deep Selectors"
.table-avatar-info::v-deep .ant-avatar-string {
	font-size: 12px;
}

.btn-status::v-deep .anticon {
	line-height: 0;
}

.table-controls {
	display: flex;
	align-items: center;
	padding: 8px 0;
}

.refresh-indicator {
	color: #666;
	font-size: 12px;
}

.table-controls .ant-btn-link {
	padding: 4px 8px;
	height: auto;
}

// External Load ID styling (removed clickable functionality for carriers)
.external-load-id {
	font-weight: 500;
	font-size: 12px;
	color: #262626;
}

// Countdown timer styles
.countdown-timer {
	font-weight: 500;
	font-size: 12px;
	line-height: 1.2;
}

.countdown-subtitle {
	font-size: 12px;
	color: #666;
	margin-top: 2px;
}

.countdown-normal {
	color: #52c41a;
}

.countdown-warning {
	color: #faad14;
}

.countdown-urgent {
	color: #ff4d4f;
	animation: pulse 2s infinite;
}

.countdown-expired {
	color: #d9d9d9;
	font-style: italic;
}

@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
	100% {
		opacity: 1;
	}
}

// Current Leader Column Styles
.current-leader-cell {
	.current-leader, .winner, .no-bids, .no-winner {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 12px;
		font-weight: 500;
		line-height: 1.4;
	}

	// Visual feedback for real-time updates
	&.bids-updated {
		background-color: #f6ffed;
		border: 1px solid #b7eb8f;
		border-radius: 4px;
		padding: 4px;
		animation: bidsUpdatePulse 2s ease-in-out;
		
		// Enhanced visual feedback for individual bidder items
		.bidder-item {
			animation: bidderItemPulse 1.5s ease-in-out;
		}
	}

		.all-bidders {
			.bidder-item {
				display: flex;
				align-items: center;
				gap: 8px;
				font-size: 12px;
				line-height: 1.4;
				margin-bottom: 6px;
				padding: 4px 0;
				border-bottom: 1px solid #f0f0f0;

				&:last-child {
					border-bottom: none;
					margin-bottom: 0;
				}

				.bidder-name {
					font-weight: 500;
					color: #1890ff;
				}

				.bid-amount {
					font-weight: 500;
					color: #52c41a;
					margin-left: auto;
					font-size: 12px;
					transition: color 0.3s ease;

					&.cancelled {
						color: #ff4d4f;
						text-decoration: line-through;
					}
				}

				// Winner highlighting - blue dot and bid amount only
				&.winner-bidder {
					.bid-amount {
						color: #1890ff;
						font-weight: 600;
					}

					.status-indicator.active {
						background-color: #1890ff;
						box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
					}

					.status-indicator.completed {
						background-color: #1890ff;
					}
				}
			}
		}

	.status-indicator {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		flex-shrink: 0;
		transition: background-color 0.3s ease, box-shadow 0.3s ease;

		&.active {
			background-color: #52c41a;
			box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.2);
		}

		&.completed {
			background-color: #8B4513;
		}

		&.cancelled {
			background-color: #ff4d4f;
			box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
		}

		&.auction_lost {
			background-color: #ff4d4f;
		}

		&.auction_won {
			background-color: #52c41a;
		}
	}

	// Company info and transportation info styles removed as they're no longer displayed

	.no-bids, .no-winner {
		color: #999;
		font-style: italic;
		font-size: 12px;
		font-weight: 500;
	}
	
	.cancelled-debug {
		color: red;
		font-size: 12px;
		font-weight: 500;
		margin-left: 4px;
	}

	.cancelled-bid {
		color: #ff4d4f !important;
		text-decoration: line-through;
		font-weight: 500;
	}
}

// My Active Bids tab styling
.ant-tabs-tab[data-key="my-bids"] {
	.ant-tabs-tab-btn {
		color: #1890ff;
		font-weight: 600;
	}
}

.ant-tabs-tab-active[data-key="my-bids"] {
	.ant-tabs-tab-btn {
		color: #1890ff !important;
		font-weight: 700;
	}
}

// Table container for horizontal scrolling
.table-container {
	overflow-x: auto;
	width: 100%;
	
	// Ensure table maintains fixed column widths
	.ant-table {
		min-width: 100%;
		
		.ant-table-thead > tr > th,
		.ant-table-tbody > tr > td {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			font-size: 12px; // Standardized font size
			font-weight: 500; // Standardized font weight
		}
		
		// Header styling
		.ant-table-thead > tr > th {
			font-weight: 600; // Slightly bolder for headers
		}
		
		// Status column styling
		.ant-table-tbody > tr > td .ant-tag {
			font-size: 12px;
			font-weight: 500;
		}
		
		// Ensure fixed columns work properly
		.ant-table-fixed-left,
		.ant-table-fixed-right {
			box-shadow: none;
		}
	}
}

// Animation for real-time bid updates
@keyframes bidsUpdatePulse {
	0% {
		background-color: #f6ffed;
		border-color: #b7eb8f;
	}
	50% {
		background-color: #d9f7be;
		border-color: #95de64;
	}
	100% {
		background-color: #f6ffed;
		border-color: #b7eb8f;
	}
}

// Animation for individual bidder items
@keyframes bidderItemPulse {
	0% {
		transform: scale(1);
		background-color: transparent;
	}
	50% {
		transform: scale(1.02);
		background-color: rgba(82, 196, 26, 0.1);
		border-radius: 4px;
	}
	100% {
		transform: scale(1);
		background-color: transparent;
	}
}


// Place Bid Button Styles
.ant-table-tbody > tr > td .ant-btn {
	font-size: 12px;
	height: 24px;
	padding: 0 8px;
	border-radius: 4px;
	font-weight: 500;
	
	&.ant-btn-primary {
		background-color: #1890ff;
		border-color: #1890ff;
		
		&:hover {
			background-color: #40a9ff;
			border-color: #40a9ff;
		}
	}
	
	&.ant-btn[disabled] {
		background-color: #f5f5f5;
		border-color: #d9d9d9;
		color: #bfbfbf;
		cursor: not-allowed;
	}
}

// My Current Bid Column Styles
.current-bid-amount {
	font-weight: 600;
	font-size: 12px;
	color: #52c41a;
}

.no-bid {
	color: #999;
	font-style: italic;
	font-size: 12px;
}
</style>